import { useEffect } from 'react';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Slices. */
import { CONNECTION_PHASE, successAlertShown } from 'shared/store/slices/socketStatusSlice';

/** Material UI Packages. */
import { Alert } from '@mui/material';

/** How long the "connected" confirmation stays on screen. */
const SUCCESS_ALERT_MS = 5000;

const alertStyle = {
    position: 'fixed',
    top: '50px',
    right: '10px',
    zIndex: 1000,
};

/**
 * Tells the operator whether what they see and save is live. While the link is
 * down the tab keeps retrying on its own, so the badge says so instead of
 * "disconnected"; once it is back, it says "syncing" until the open screens have
 * re-read what teammates changed in the meantime, then confirms "connected".
 */
export default function SocketAlert() {
    const dispatch = useDispatch();
    /** State for showing the connection status of the WebSocket (fields only, so unrelated slice changes do not redraw). */
    const phase = useSelector((state) => state.socketStatus.phase);
    const showSuccessAlert = useSelector((state) => state.socketStatus.showSuccessAlert);

    useEffect(() => {
        if (!showSuccessAlert) return undefined;
        const timer = setTimeout(() => dispatch(successAlertShown()), SUCCESS_ALERT_MS);
        return () => clearTimeout(timer);
    }, [showSuccessAlert, dispatch]);

    if (phase === CONNECTION_PHASE.REFUSED) {
        return (
            <Alert severity="error" sx={{ ...alertStyle, color: 'red' }}>
                Connection refused. Please sign in again.
            </Alert>
        );
    }

    if (phase === CONNECTION_PHASE.RECONNECTING) {
        return (
            <Alert severity="error" sx={{ ...alertStyle, color: 'red' }}>
                Reconnecting…
            </Alert>
        );
    }

    if (phase === CONNECTION_PHASE.SYNCING) {
        return (
            <Alert severity="info" sx={alertStyle}>
                Syncing…
            </Alert>
        );
    }

    if (phase === CONNECTION_PHASE.CONNECTED && showSuccessAlert) {
        return (
            <Alert severity="success" sx={{ ...alertStyle, color: 'green' }}>
                Connected
            </Alert>
        );
    }

    return null;
}
